import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminHttpClient } from '../../http.js';
import type { ExtensionReleaseView, ToastMessage } from '../../types.js';
import { describeError } from '../../errors.js';
import { LOCAL_EXTENSION_CATALOG } from '../../catalogs.js';

export function ExtensionReleasesTab({ http, onToast, isActive }: { http: AdminHttpClient; onToast: (toast: ToastMessage) => void; isActive: boolean }) {
  const { t } = useTranslation();
  const [extensionId, setExtensionId] = useState(LOCAL_EXTENSION_CATALOG[0]?.id ?? 'hwp');
  const [platform, setPlatform] = useState<'win32' | 'darwin'>('win32');
  const [releaseState, setReleaseState] = useState<{ extensionId: string; releases: ExtensionReleaseView[] }>({ extensionId, releases: [] });
  const [busy, setBusy] = useState(false);
  const requestSeq = useRef(0);
  const load = useCallback(async () => {
    const request = ++requestSeq.current;
    const targetExtensionId = extensionId;
    try {
      const releases = await http.listExtensionReleases(targetExtensionId, platform);
      if (request === requestSeq.current) setReleaseState({ extensionId: targetExtensionId, releases });
    }
    catch (error) {
      if (request === requestSeq.current) onToast({ tone: 'error', message: describeError(error, t) });
    }
  }, [http, onToast, t, extensionId, platform]);
  useEffect(() => { if (isActive) void load(); }, [isActive, load]);
  const releases = releaseState.extensionId === extensionId ? releaseState.releases : [];
  const stable = releases.find((release) => release.stable);
  const change = async (mode: 'promote' | 'rollback', version: string) => {
    const phrase = `${mode.toUpperCase()} ${extensionId} ${version}`;
    if (window.prompt(t('extensionReleases.changePrompt', { phrase }), '') !== phrase) return;
    setBusy(true);
    try {
      if (mode === 'rollback') await http.rollbackExtensionRelease(extensionId, version, platform);
      else await http.promoteExtensionRelease(extensionId, version, platform);
      await load();
    } catch (error) { onToast({ tone: 'error', message: describeError(error, t) }); }
    finally { setBusy(false); }
  };
  const withdraw = async () => {
    if (window.prompt(t('extensionReleases.withdrawPrompt', { extension: extensionId }), '') !== `WITHDRAW ${extensionId}`) return;
    setBusy(true);
    try { await http.withdrawExtensionRelease(extensionId, platform); await load(); }
    catch (error) { onToast({ tone: 'error', message: describeError(error, t) }); }
    finally { setBusy(false); }
  };
  return <div className="panel">
    <div className="section-heading"><div><h2>{t('extensionReleases.title')}</h2><p>{t('extensionReleases.help')}</p></div><button type="button" className="secondary danger" disabled={busy || !stable} onClick={() => void withdraw()}>{t('extensionReleases.withdraw')}</button></div>
    <label className="field">
      <span>{t('extensionReleases.extension')}</span>
      <select value={extensionId} disabled={busy} onChange={(event) => setExtensionId(event.target.value)}>
        {LOCAL_EXTENSION_CATALOG.map((extension) => <option key={extension.id} value={extension.id}>{extension.label}</option>)}
      </select>
    </label>
    <label className="field"><span>{t('extensionReleases.platform')}</span><select value={platform} disabled={busy} onChange={(event) => setPlatform(event.target.value as 'win32' | 'darwin')}><option value="win32">Windows</option><option value="darwin">macOS</option></select></label>
    <div className="table-wrap"><table><thead><tr><th>{t('extensionReleases.version')}</th><th>{t('extensionReleases.protocol')}</th><th>{t('extensionReleases.published')}</th><th>{t('extensionReleases.action')}</th></tr></thead><tbody>{releases.map((release) => {
      const older = !!stable && Date.parse(release.publishedAt) < Date.parse(stable.publishedAt);
      return <tr key={release.version}><td><strong>{release.version}</strong>{release.stable ? <span className="badge active">stable</span> : null}</td><td>{release.protocolVersion}</td><td>{new Date(release.publishedAt).toLocaleString()}</td><td>{!release.stable ? <button type="button" className="secondary" disabled={busy} onClick={() => void change(older ? 'rollback' : 'promote', release.version)}>{older ? t('extensionReleases.rollback') : t('extensionReleases.promote')}</button> : null}</td></tr>;
    })}</tbody></table></div>
    {!releases.length ? <p className="muted">{t('extensionReleases.empty', { extension: extensionId })}</p> : null}
  </div>;
}
